import React, { useEffect, useState } from 'react'
import {
  Box,
  Card,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { LoadingButton } from '@mui/lab'
import api from 'services/api'
import { DataGrid } from 'pages/Atlhetes/AthleteRoutineList/DataGrid'
import { ColumnsToAssign } from 'pages/Atlhetes/AthleteRoutineList/ColumnsToAssign'
import moment from 'moment'
import { useAppDispatch } from 'redux/hooks'
import { showMessage } from 'redux/reducers/snackbar/snackbar.actions'
import { Button } from 'components/Button'
import { DataGridHeader } from './components/DataGridHeader'
import { TreatEmpty } from 'components/TreatEmpty'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import useResponsive from 'hooks/useResponsive'
import { Routine } from 'models/interfaces/routine.interface'

type ProfessionalAthleteProps = {
  professionalAthleteId: number
  name: string
  note: string
  startedAt: string
  dueDateAt: string
  visibleAfterDueDate: boolean
}

type Props = {
  isVisible: boolean
  professionalAthleteToAssign: any
  onLoadRoutinesAfterCreate: (value: boolean) => void
  loading: boolean
  onClose: () => void
}

export const AssignRoutineToModal = ({ isVisible, professionalAthleteToAssign, onLoadRoutinesAfterCreate, loading, onClose }: Props) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [routineSelected, setRoutineSelected] = useState<any>()
  const [routineToAssignResult, setRoutineToAssignResult] = useState<[]>([])
  const [data, setData] = useState<[]>([])
  const theme = useTheme()
  const mdUp = useResponsive('up', 'md')

  useEffect(() => {
    onLoadRoutinesAfterCreate(false)
    if (isVisible) {
      dataFetch().catch(console.error)
    }
  }, [isVisible])

  const dataFetch = async () => {
    const { data } = await api.get('routines')
    const dataTranslation = data.map(
      (routine: Routine) => ({ ...routine, level: t(`routine.level.${routine?.level.toLowerCase()}`).toUpperCase(), type: t(`routine.types.${routine?.type.toLowerCase()}`).toUpperCase() })
    )
    setData(dataTranslation)
    setRoutineToAssignResult(dataTranslation)
  }

  const handleSearch = (e: any) => {
    if (!e.target.value) {
      setRoutineToAssignResult(data)
      return
    }
    setRoutineToAssignResult([])
    const dataFound: any = []
    const searchText = e.target.value
    const regex = new RegExp(searchText.normalize('NFD').replace(/[\u0300-\u036f]/g, ''), 'i')
    data.forEach((obj) => {
      Object.entries(obj).forEach(([key, value]) => {
        const textValue: any = value
        if (!dataFound.includes(obj) &&
          (key === 'name' || key === 'level' || key === 'note') &&
          regex.exec(textValue.normalize('NFD').replace(/[\u0300-\u036f]/g, ''))) {
          dataFound.push(obj)
        }
      })
    })
    setRoutineToAssignResult(dataFound)
  }

  const handleAddRoutineToAthlete = async () => {
    if (!routineSelected) {
      dispatch(showMessage({ message: `${t('routine.selectToAssign')}`, type: 'error' }))
      return
    } else if (!professionalAthleteToAssign) {
      dispatch(showMessage({ message: `${t('athlete.selectToAssign')}`, type: 'error' }))
      return
    }
    try {
      const data: ProfessionalAthleteProps[] = []
      data.push({
        professionalAthleteId: professionalAthleteToAssign,
        name: routineSelected.name,
        note: routineSelected.note,
        startedAt: (routineSelected.startedAt ? moment(routineSelected.startedAt, 'DD/MM/YYYY').format('YYYY-MM-DD') : moment(new Date(), 'DD/MM/YYYY').format('YYYY-MM-DD')),
        dueDateAt: (routineSelected.dueDateAt ? moment(routineSelected.dueDateAt, 'DD/MM/YYYY').format('YYYY-MM-DD') : ''),
        visibleAfterDueDate: routineSelected.visibleAfterDueDate
      })
      await api.post(`/routines/${routineSelected.id}/import-professional-athlete`,
        { professionalAthleteRoutineParams: data },
        { headers: { 'Content-Type': 'application/json' } })
      onLoadRoutinesAfterCreate(true)
      onClose()
      setRoutineSelected('')
      dispatch(showMessage({ message: `${t('routine.assigned_success')}` }))
    } catch (e) {
      dispatch(showMessage({ message: `${t('general.error')}`, type: 'error' }))
    }
  }

  const handleAddRoutine = () => navigate('/routine')

  return (
    <Box>
      <Dialog
        fullWidth={true}
        open={isVisible}
        maxWidth="lg"
        onClose={onClose}
        aria-labelledby="customized-dialog-title"
      >
        <DialogTitle>{`${t('routine.title.assignToAthlete')}`}</DialogTitle>
        <DialogContent>
          <Container>
            <Box marginTop={ 2 }>
              { data.length > 0
                ? <Card variant="outlined">
                  <Stack spacing={1.5} sx={{ padding: { xs: 0.5, sm: 1 } }} >
                    <DataGridHeader
                      onActionSearch={(value) => handleSearch(value)}
                      onSearchTitle={`${t('search.titleRoutine')}`}
                      onActionTitle={`${t('athlete.title.addAthlete')}`}
                    />
                    <Typography color={theme.palette.primary.main} sx={{ pl: '7px', pr: '5px' }}>
                      <b>{t('general.message.editCells')}</b>
                    </Typography>
                    <DataGrid
                      toAssign={true}
                      setRoutineSelected={setRoutineSelected}
                      disableColumnMenu={true}
                      rows={routineToAssignResult}
                      columns={mdUp
                        ? ColumnsToAssign
                        : ColumnsToAssign.filter(
                          (column) => (
                            column.field !== 'image' &&
                            column.field !== 'note' &&
                            column.field !== 'level' &&
                            column.field !== 'startedAt' &&
                            column.field !== 'dueDateAt' &&
                            column.field !== 'visibleAfterDueDate'
                          )
                        )
                      }
                      hideFooterSelectedRowCount={mdUp ? false : true}
                    />
                  </Stack>
                </Card>
                : <Stack spacing={2}>
                  <TreatEmpty
                    titleMessage={ t('athlete.title.routinesEmpty') }
                    subTitleMessage={ t('routine.subTitle.trainingBench_empty') }
                    buttons={ [{ title: t('routine.newRoutine'), action: handleAddRoutine }] }
                  />
                </Stack>
              }
            </Box>
          </Container>
        </DialogContent>
        <DialogActions>
          { routineToAssignResult.length > 0 &&
            <LoadingButton loading={loading} variant="contained" onClick={handleAddRoutineToAthlete}>
              {t('general.btn.save')}
            </LoadingButton>
          }
          <Button title={t('general.btn.dismiss')} variant="contained" color="secondary" onClick={onClose} />
        </DialogActions>
      </Dialog>
    </Box>
  )
}
